/**
 * The **Pull list** overlay — the PULL STATION (pulled-blanks v2 §2.5 door 1, D1/D2/D18). What the batch
 * card's "Pull list" button, the screen-print run page's, the label sheet's post-print door and the card's
 * "Tick the pull list" / "Edit rows" open. Fetches the batch manifest (re-nested server-side; the same query
 * the label sheet uses), waits for the blank catalogue so rows are NAMED rather than titled, and shows the
 * rows with a live *Got* column the moment they land.
 *
 * It NEVER prints by itself (D18 — the owner: an iPad or phone at the shelf must not be shown a print sheet
 * to dismiss). *Print* is a toolbar button for whoever wants paper; the desk pays one tap. Opening posts the
 * `station` stamp and *Print* posts `pull-list`, both advisory and fail-open (`stampPull`).
 *
 * The on-screen table IS the record: each row's *Got* cell is a button — one tap = *got all N*, a second
 * tap opens *got __ of N* for a short — and **every row is ticked; there is no sheet-level shortcut (D2)**.
 * Each tick posts the COMPLETE record (`pullRecordRows`: the displayed rows' bare skus and the quantity the
 * puller tapped or typed, never a cached figure) with the revision on screen — a whole-map compare-and-set,
 * so two pullers on one sheet cannot overwrite each other: the loser gets 409 `pull_changed` with the
 * current record and sees it. A short opens the shelf question (D17, `PullShortShelfDialog`).
 *
 * Rendered into a top-level `<body>` portal with id `pull-list-print` so the print stylesheet can isolate
 * it: on print every OTHER body child is `display:none`, leaving only this panel in NORMAL flow (Tailwind
 * `print:static`), so nothing of the app shell leaks into the printout.
 */
import { BatchPulledMapSchema } from '@fsp/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Printer, X } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import {
  type BatchManifest,
  type BatchPulledMap,
  type BatchPulledVia,
  type BlankPull,
  blanksToPull,
  type PullRowState,
  pullRecordRows,
  pullRowStates,
  pullState,
  type ScreenPrintWorkItem,
} from './batch-lib';
import { invalidatePullItems } from './inventory-lib';
import { PullListSheet } from './pull-list-sheet';
import { PullShortShelfDialog, type PullShortTarget } from './pull-short-shelf-dialog';
import { stampPull } from './pull-stamp';
import { useBlankNamesQuery } from './use-blank-names';

/** The record as the overlay holds it: the map, the revision it was read at, the first tick. */
type PullRecord = { pulled: BatchPulledMap; rev: number | null; pulledAt?: string };

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

/**
 * A 409 `pull_changed` carries the CURRENT record (`current: {pulled, pulledRev, pulledAt, status}`): the
 * compare-and-set lost to another puller or tab, and the overlay shows theirs instead of overwriting it.
 * Narrowed field by field — the error context is untyped on the wire.
 */
function parsePullChanged(error: { type: string; [key: string]: unknown }): PullRecord | null {
  if (error.type !== 'pull_changed') return null;
  const current = isRecord(error.current) ? error.current : {};
  const map = BatchPulledMapSchema.safeParse(current.pulled);
  return {
    pulled: map.success ? map.data : {},
    rev: typeof current.pulledRev === 'number' ? current.pulledRev : null,
    pulledAt: typeof current.pulledAt === 'string' ? current.pulledAt : undefined,
  };
}

function clockLabel(iso: string, nowMs: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const sameDay = d.toDateString() === new Date(nowMs).toDateString();
  return sameDay
    ? `today ${time}`
    : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
}

/** "office" from "office@fastscreenprint.com" — a Cognito sub is not worth showing. */
function loginLabel(by: string | undefined): string | null {
  if (!by) return null;
  const at = by.indexOf('@');
  return at > 0 ? `${by.slice(0, at)} login` : null;
}

export function PullList({
  batchId,
  label,
  screenPrintWork,
  onClose,
  via = 'pull-list',
}: {
  batchId: string;
  label: string;
  /** The batch's screen-print placements — see `PullListSheet`; `[]` on an all-DTF batch. */
  screenPrintWork: readonly ScreenPrintWorkItem[];
  onClose: () => void;
  /** Which door opened the station — recorded on every row ticked from it (§2.2 `via`). */
  via?: BatchPulledVia;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const manifestQuery = useQuery({
    queryKey: ['batch-manifest', batchId],
    queryFn: async () => {
      const { data, error } = await client.GET('/batches/{batchId}/manifest', {
        params: { path: { batchId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the pull list.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data as BatchManifest;
    },
  });
  // The catalogue's LOAD STATE, not just its map: `useBlankNames` hands back an empty map while the
  // query is pending, which is indistinguishable from a resolved-but-empty catalogue — and a row ticked
  // on that render would still key by its bare sku, but the puller would be reading product titles.
  const names = useBlankNamesQuery();

  // Escape closes the overlay (matches the app's dialog behaviour).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const m = manifestQuery.data;
  // D18: the station's first open is the third advisory stamp — a paperless pull nobody ticked still turns
  // amber and is the count's one question. Only once the manifest says READY: the run page opens this
  // overlay for a DRAFT / COMPOSITING run and the card for a closed sheet, and a stamp there would read
  // "pull station opened · not confirmed" on a sheet nobody could pull (review finding). Once per mount
  // (a ref, so a re-render with a fresh client instance cannot re-post it), fail-open, never awaited.
  const stamped = useRef(false);
  const manifestStatus = m?.status;
  useEffect(() => {
    if (stamped.current || manifestStatus !== 'READY') return;
    stamped.current = true;
    void stampPull(client, batchId, 'station');
  }, [client, batchId, manifestStatus]);
  // The catalogue has ANSWERED — with names, or with an error (rows then fall back to product titles,
  // which is a legible page; waiting forever for a catalogue that is down is not).
  const namesSettled = names.isResolved || names.isError;
  // The manifest is often CACHED from the label sheet or the picker (shared key) and refetching when this
  // opens: Print waits for that refetch, or the paper would snapshot rows a cancellation has since removed.
  const ready = m != null && namesSettled && !manifestQuery.isFetching;

  // The record on screen. Adopt the server's whenever the manifest carries a NEWER revision than the one
  // shown — the first load, a change-feed refetch after another puller's tick. Our own writes land through
  // the mutation's response, so a refetch that resolves late with an OLDER revision cannot roll a tick back.
  const [record, setRecord] = useState<PullRecord | null>(null);
  useEffect(() => {
    if (!m) return;
    const rev = m.pulledRev ?? null;
    setRecord((cur) => {
      if (cur && (rev ?? -1) <= (cur.rev ?? -1)) return cur;
      return { pulled: m.pulled ?? {}, rev, pulledAt: m.pulledAt };
    });
  }, [m]);

  const rows = useMemo(
    () => (m ? blanksToPull(m, names.nameBySku, screenPrintWork) : []),
    [m, names.nameBySku, screenPrintWork],
  );
  const states = useMemo(() => pullRowStates(rows, record?.pulled), [rows, record]);
  const now = Date.now();
  const summary =
    m && record
      ? pullState(
          {
            status: m.status,
            pulled: record.pulled,
            pulledAt: record.pulledAt,
            pulledRev: record.rev ?? undefined,
            pullListPrintedAt: m.pullListPrintedAt,
            labelsPrintedAt: m.labelsPrintedAt,
            pullStationOpenedAt: m.pullStationOpenedAt,
          },
          rows,
        )
      : null;

  const [who, setWho] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [shortFor, setShortFor] = useState<PullShortTarget | null>(null);
  const [printedThisSession, setPrintedThisSession] = useState<string | null>(null);

  const write = useMutation({
    mutationFn: async (vars: { ssSku: string; qty: number | null; row: BlankPull }) => {
      // The complete record after this change — the displayed rows' bare skus, the quantity the puller
      // tapped or typed for the changed row, every other recorded row unchanged (§2.3).
      const bodyRows = pullRecordRows(record?.pulled, vars);
      const res = await client.POST('/batches/{batchId}/pulled', {
        params: { path: { batchId } },
        body: { rev: record?.rev ?? null, rows: bodyRows, via, who: who.trim() || undefined },
      });
      if (res.error) {
        const changed = parsePullChanged(res.error.error);
        if (changed) {
          return {
            kind: 'changed' as const,
            record: changed,
            detail:
              res.error.error.detail ??
              "This sheet's pull record changed since you loaded it — showing the current record.",
          };
        }
        if (res.response?.status === 404) {
          // The route is live; a 404 is the SHEET — deleted or rebuilt under the puller.
          throw new Error(
            'This sheet no longer exists — it was deleted or rebuilt. Close this and open the pull list from the rebuilt sheet.',
          );
        }
        throw new Error(res.error.error.detail ?? 'Could not record the pull.');
      }
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      const b = res.data.data;
      return {
        kind: 'ok' as const,
        record: { pulled: b.pulled ?? {}, rev: b.pulledRev ?? null, pulledAt: b.pulledAt },
        change: vars,
      };
    },
    onSuccess: (out) => {
      setRecord(out.record);
      // The shared manifest cache carries the record for the next mount (the label sheet's door, a
      // reopen); the card and the rail re-read the header.
      qc.setQueryData<BatchManifest>(['batch-manifest', batchId], (old) =>
        old
          ? {
              ...old,
              pulled: out.record.pulled,
              pulledRev: out.record.rev ?? undefined,
              pulledAt: out.record.pulledAt,
            }
          : old,
      );
      qc.invalidateQueries({ queryKey: ['batch', batchId] });
      qc.invalidateQueries({ queryKey: ['batches'] });
      // The hub's "pull started but not confirmed" item clears on its next visit, not in five minutes.
      invalidatePullItems(qc);
      setEditing(null);
      if (out.kind === 'changed') {
        setNotice(out.detail);
        return;
      }
      setNotice(null);
      const { row, qty } = out.change;
      // A short — fewer came off the shelf than the list said — asks the one more thing (D17).
      if (row.ssSku && qty != null && qty < row.qty) {
        setShortFor({
          ssSku: row.ssSku,
          label: row.label,
          variant: row.variant || row.sizeBand,
          listed: row.qty,
          got: qty,
          sheetLabel: label,
          batchId,
          rev: out.record.rev ?? 0,
        });
      }
    },
  });

  const print = () => {
    setPrintedThisSession(new Date().toISOString());
    void stampPull(client, batchId, 'pull-list');
    window.print();
  };

  const printedAt = printedThisSession ?? m?.pullListPrintedAt;
  const unmapped = states.filter((s) => !s.row.ssSku);
  const tickable = states.filter((s) => s.row.ssSku);
  const listedTotal = tickable.reduce((n, s) => n + s.row.qty, 0);
  const remaining =
    summary?.kind === 'pulled' ? (summary.remaining ?? tickable.length) : tickable.length;

  // ── the live Got cell ─────────────────────────────────────────────────────────────────────────────
  const got = (row: BlankPull): ReactNode => {
    const s = states.find((x) => x.row.key === row.key);
    if (!s || !row.ssSku) {
      return <span className="text-[10px] leading-tight text-[#625d57]">no blank on file</span>;
    }
    const busy = write.isPending && write.variables?.ssSku === row.ssSku;
    const name = `${row.label} ${row.variant || row.sizeBand}`;
    if (!s.recorded) {
      return (
        <button
          type="button"
          aria-label={`Got all ${row.qty} — ${name}`}
          disabled={busy || write.isPending}
          onClick={() => write.mutate({ ssSku: row.ssSku ?? '', qty: row.qty, row })}
          className="w-full rounded-[4px] border border-dashed border-[#9b958d] px-1.5 py-1 text-[10.5px] leading-tight text-[#625d57] hover:border-[#141210] hover:text-[#141210] disabled:opacity-50"
        >
          tap when gathered
        </button>
      );
    }
    const text =
      s.short > 0
        ? `got ${s.recorded.qty} of ${row.qty} · ${s.short} short`
        : s.over > 0
          ? `got ${s.recorded.qty} · ${row.qty} owed now`
          : `got all ${row.qty}`;
    return (
      <span className="flex flex-col items-stretch gap-1">
        <button
          type="button"
          aria-label={`Edit ${name} — got ${s.recorded.qty} of ${row.qty}`}
          disabled={busy}
          onClick={() => setEditing(row.ssSku ?? null)}
          className={cn(
            'inline-flex w-full items-center justify-center gap-1 rounded-[4px] border px-1.5 py-1 text-[10.5px] font-semibold leading-tight',
            s.short > 0
              ? 'border-amber-500 bg-amber-50 text-amber-800'
              : 'border-emerald-600 bg-emerald-50 text-emerald-800',
          )}
        >
          <Check className="size-3 shrink-0" />
          {text}
        </button>
        {editMode && (
          <button
            type="button"
            aria-label={`Untick ${name}`}
            disabled={busy}
            onClick={() => write.mutate({ ssSku: row.ssSku ?? '', qty: null, row })}
            className="rounded-[4px] border border-[#9b958d] px-1.5 py-0.5 text-[10px] text-[#625d57] hover:text-[#141210]"
          >
            Untick
          </button>
        )}
      </span>
    );
  };

  const after = (row: BlankPull): ReactNode => {
    if (!row.ssSku || editing !== row.ssSku) return null;
    const s = states.find((x) => x.row.key === row.key);
    return (
      <RowEditor
        state={s}
        row={row}
        busy={write.isPending}
        onSave={(qty) => write.mutate({ ssSku: row.ssSku ?? '', qty, row })}
        onCancel={() => setEditing(null)}
      />
    );
  };

  return createPortal(
    <div
      id="pull-list-print"
      role="dialog"
      aria-modal="true"
      aria-label={`Pull list for ${label}`}
      className="fixed inset-0 z-50 overflow-auto bg-background p-4 sm:p-6 print:static print:overflow-visible print:bg-transparent print:p-0"
    >
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
          <div>
            <h3 className="text-base font-semibold">Pull list — {label}</h3>
            <p className="text-xs text-muted-foreground">
              {printedAt
                ? `Printed ${clockLabel(printedAt, now)} · Print again for another copy.`
                : 'Tick each row as it comes off the shelf · Print for a paper copy — nothing prints by itself.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={print} disabled={!ready}>
              <Printer className="size-4" />
              Print
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="size-4" />
              Close
            </Button>
          </div>
        </div>

        {manifestQuery.isPending && <Skeleton className="h-40 w-full" />}
        {manifestQuery.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn't load the pull list</AlertTitle>
            <AlertDescription>
              {manifestQuery.error instanceof Error
                ? manifestQuery.error.message
                : 'Network error.'}
            </AlertDescription>
          </Alert>
        )}

        {m && (
          <>
            {/* Screen-only warnings — the page itself simply leaves these garments out. */}
            {m.oversizedCount > 0 && (
              <Alert variant="destructive" className="print:hidden">
                <AlertDescription>
                  {m.oversizedCount} design{m.oversizedCount === 1 ? '' : 's'} won't fit the roll
                  and {m.oversizedCount === 1 ? 'is' : 'are'} missing from this list — re-check the
                  batch.
                </AlertDescription>
              </Alert>
            )}
            {m.cancelledTransferCount > 0 && (
              <Alert variant="destructive" className="print:hidden">
                <AlertTitle>
                  {m.cancelledTransferCount} transfer{m.cancelledTransferCount === 1 ? '' : 's'}{' '}
                  {m.cancelledTransferCount === 1 ? 'is' : 'are'} for a CANCELLED order
                </AlertTitle>
                <AlertDescription>
                  The order was cancelled after this sheet was built. Its blanks are left off the
                  pull list — do not print or press it.
                </AlertDescription>
              </Alert>
            )}
            {m.qtyDroppedTransferCount > 0 && (
              <Alert variant="destructive" className="print:hidden">
                <AlertTitle>
                  {m.qtyDroppedTransferCount} transfer
                  {m.qtyDroppedTransferCount === 1 ? '' : 's'}{' '}
                  {m.qtyDroppedTransferCount === 1 ? 'was' : 'were'} refunded after building
                </AlertTitle>
                <AlertDescription>
                  Those copies are on the film but no longer owed. Their blanks are left off the
                  pull list — do not press them.
                </AlertDescription>
              </Alert>
            )}
            {!namesSettled && (
              <p className="text-xs text-muted-foreground print:hidden">Naming the blanks…</p>
            )}

            {/* The station bar — live from the first render; the record's state in the plan's words. */}
            {summary && (m.status === 'READY' || m.status === 'PRINTED') && (
              <section
                aria-label="Pull record"
                aria-live="polite"
                className={cn(
                  'rounded-md border px-3 py-2 text-sm print:hidden',
                  summary.kind === 'pulled' && summary.complete
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100'
                    : 'border-border bg-muted/30',
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <div className="font-semibold">
                    {summary.kind === 'pulled' && summary.complete ? (
                      <>
                        Pulled {summary.units}
                        {summary.at ? ` · ${clockLabel(summary.at, now)}` : ''}
                        {loginLabel(summary.by) ? ` · ${loginLabel(summary.by)}` : ''}
                        {summary.who ? ` · by ${summary.who}` : ''}
                      </>
                    ) : (
                      <>
                        Pulled · {summary.kind === 'pulled' ? summary.units : 0} of {listedTotal}{' '}
                        blanks · {remaining} {remaining === 1 ? 'row' : 'rows'} to tick
                        {summary.kind === 'pulled' && summary.shorts.length > 0
                          ? ` · ${summary.shorts.length} size${summary.shorts.length === 1 ? '' : 's'} short`
                          : ''}
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label
                      htmlFor="pull-who"
                      className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      Pulled by (optional)
                      <Input
                        id="pull-who"
                        value={who}
                        onChange={(e) => setWho(e.target.value)}
                        maxLength={8}
                        placeholder="initials"
                        className="h-7 w-20 text-xs"
                      />
                    </label>
                    {summary.kind === 'pulled' && (
                      <Button
                        size="sm"
                        variant={editMode ? 'secondary' : 'outline'}
                        aria-pressed={editMode}
                        onClick={() => setEditMode((v) => !v)}
                      >
                        Edit rows
                      </Button>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {summary.kind === 'pulled' && summary.complete
                    ? `${summary.ticked} row${summary.ticked === 1 ? '' : 's'} ticked · ${summary.units} blanks are off the shelf and still in the book until pressed. Counts add them back for you.`
                    : 'Tap each row as it comes off the shelf. Every row is ticked — there is no one-tap shortcut. These blanks stay in the book until pressed.'}
                </p>
                {summary.kind === 'pulled' &&
                  summary.shorts.map((s) => (
                    <p key={s.ssSku} className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                      {s.listed - s.got} {s.label} {s.variant.replace(/ · /g, ' ')} short — the
                      press will wait for them; tap got {s.listed - s.got} more here when they
                      arrive.
                    </p>
                  ))}
                {unmapped.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {unmapped.length} row{unmapped.length === 1 ? ' has' : 's have'} no blank on
                    file — map the product in Catalog first.{' '}
                    {unmapped.length === 1 ? 'It never blocks' : 'They never block'} the other rows.
                  </p>
                )}
                {notice && (
                  <p
                    role="status"
                    className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300"
                  >
                    {notice}
                  </p>
                )}
                {write.isError && (
                  <p role="alert" className="mt-1 text-xs font-medium text-destructive">
                    {write.error instanceof Error
                      ? write.error.message
                      : 'Could not record the pull.'}
                  </p>
                )}
              </section>
            )}

            <PullListSheet
              label={label}
              manifest={m}
              screenPrintWork={screenPrintWork}
              nameBySku={names.nameBySku}
              got={m.status === 'READY' || m.status === 'PRINTED' ? got : undefined}
              after={after}
            />
          </>
        )}
      </div>
      {shortFor && <PullShortShelfDialog target={shortFor} onClose={() => setShortFor(null)} />}
    </div>,
    document.body,
  );
}

/** *got __ of N* — the short / correction editor under a row. Saves ONLY the typed figure, capped at the
 *  listed quantity (the server caps at the sheet's frozen quantity besides). */
function RowEditor({
  state,
  row,
  busy,
  onSave,
  onCancel,
}: {
  state: PullRowState | undefined;
  row: BlankPull;
  busy: boolean;
  onSave: (qty: number | null) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(String(state?.recorded?.qty ?? row.qty));
  const n = /^\d{1,4}$/.test(draft.trim()) ? Number.parseInt(draft, 10) : null;
  const valid = n != null && n >= 1 && n <= row.qty;
  const short = n != null ? Math.max(0, row.qty - n) : 0;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#1a1714]">
      <span className="font-semibold">
        {row.label} · {row.variant || row.sizeBand}
      </span>
      <span className="text-[#625d57]">
        Listed <b className="text-[#1a1714]">{row.qty}</b>. How many came off the shelf?
      </span>
      <Input
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label={`How many ${row.label} ${row.variant || row.sizeBand} came off the shelf`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-8 w-16 bg-white text-center tabular-nums"
        aria-invalid={draft.trim() !== '' && !valid ? true : undefined}
      />
      <span className="text-[#625d57] tabular-nums">
        of {row.qty}
        {short > 0 ? ` · ${short} short` : ''}
      </span>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onSave(row.qty)}>
        Got all {row.qty}
      </Button>
      <Button size="sm" disabled={busy || !valid || n === row.qty} onClick={() => onSave(n)}>
        Got {valid ? n : '…'}
      </Button>
      {state?.recorded && (
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onSave(null)}>
          Untick
        </Button>
      )}
      <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
